import { GridRowsProp } from "@mui/x-data-grid";
import { motion } from "motion/react";
import { useCallback, useMemo } from "react";

import StatusFilterItem from "@/components/table/StatusFilterItem";
import { operationStatuses } from "@/constants/operationStatuses";
import { PlanFilterStatuses, usePlanActions, usePlanFilters } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

import { CommonFilterSwitch } from "../../types";

export function PlanStatusFilter({ allRows }: { allRows: GridRowsProp }) {
    const filters = usePlanFilters();
    const arrivalStatus = useMemo(
        () =>
            filters.isArrival === CommonFilterSwitch.ALL || filters.airport
                ? { valid: false, value: false }
                : filters.isArrival === CommonFilterSwitch.FALSE
                  ? { valid: true, value: false }
                  : { valid: true, value: true },
        [filters]
    );
    const { updateFilters } = usePlanActions();

    const activeStyle = useMemo(() => {
        for (const [index, item] of operationStatuses.entries()) {
            if (
                item.cond(
                    filters.cancelled === CommonFilterSwitch.TRUE ? true : false,
                    arrivalStatus.value,
                    arrivalStatus.valid
                )
            )
                return { translateX: `${index * 100}%`, backgroundColor: item.hover };
        }
        return false;
    }, [arrivalStatus, filters]);

    const vals = useMemo(
        () =>
            allRows.reduce(
                (prev, curr) => {
                    const value = operationStatuses.find((item) =>
                        item.cond(
                            (curr as IPlanPassenger<"api">).cancelled,
                            (curr as IPlanPassenger<"api">).isFlightArrival,
                            true
                        )
                    )?.value;
                    if (value) prev[value] += 1;
                    return prev;
                },
                {
                    [PlanFilterStatuses.ALL]: 0,
                    [PlanFilterStatuses.ARRIVAL]: 0,
                    [PlanFilterStatuses.DEPARTURE]: 0,
                    [PlanFilterStatuses.CANCELLED]: 0,
                }
            ),
        [allRows]
    );

    const handleUpdate = useCallback(
        (status: PlanFilterStatuses, active?: boolean) => {
            let newFilters = { ...filters, airport: null };
            if (active) {
                newFilters.isArrival = CommonFilterSwitch.ALL;
                newFilters.cancelled = CommonFilterSwitch.ALL;
            } else
                switch (status) {
                    case PlanFilterStatuses.ALL:
                        newFilters.isArrival = CommonFilterSwitch.ALL;
                        newFilters.cancelled = CommonFilterSwitch.ALL;
                        break;
                    case PlanFilterStatuses.DEPARTURE:
                        newFilters.isArrival = CommonFilterSwitch.FALSE;
                        newFilters.cancelled = CommonFilterSwitch.FALSE;
                        break;
                    case PlanFilterStatuses.ARRIVAL:
                        newFilters.isArrival = CommonFilterSwitch.TRUE;
                        newFilters.cancelled = CommonFilterSwitch.FALSE;
                        break;
                    case PlanFilterStatuses.CANCELLED:
                        newFilters.isArrival = CommonFilterSwitch.ALL;
                        newFilters.cancelled = CommonFilterSwitch.TRUE;
                        break;
                }
            updateFilters(newFilters);
        },
        [filters]
    );
    const filterElements = useMemo(
        () =>
            operationStatuses.map((item) => (
                <StatusFilterItem<PlanFilterStatuses>
                    key={item.tooltip.title}
                    {...item}
                    active={item.cond(
                        filters.cancelled === CommonFilterSwitch.TRUE && filters.isArrival === CommonFilterSwitch.ALL
                            ? true
                            : false,
                        arrivalStatus.value,
                        arrivalStatus.valid
                    )}
                    count={vals[item.value]}
                    handleUpdate={handleUpdate}
                />
            )),
        [handleUpdate, vals, filters, arrivalStatus]
    );
    return (
        <div className=" relative py-1 grid grid-cols-3 bg-gray-100/80  divide-x divide-gray-300    rounded">
            {filterElements}
            {activeStyle && (
                <motion.div
                    layoutId="plan-status-filter"
                    id="plan-status-filter"
                    animate={activeStyle}
                    transition={{ type: "tween" }}
                    className="  rounded-none absolute   !self-center  !h-[calc(100%-8px)]     !w-1/3 "
                ></motion.div>
            )}
        </div>
    );
}
